import { Notification } from '../services/Notification.js';
import { translate } from './i18n.js';
import { locales } from './locales.js';

const HOME_NAVIGATION = [
    {
        id: 'about',
        icon: '/images/about-grey.svg',
        titleKey: 'mainMenu.about',
        route: 'about',
        type: 'modal'
    },
    {
        id: 'eventsList',
        icon: '/images/list-grey.svg',
        titleKey: 'home.listTitle',
        route: '',
        type: 'tab',
        view: 'list'
    },
    {
        id: 'eventsCalendar',
        icon: '/images/calendar-grey.svg',
        titleKey: 'home.calendarTitle',
        route: 'events/calendar',
        type: 'tab',
        view: 'calendar'
    },
    {
        id: 'eventsMap',
        icon: '/images/marker-grey.svg',
        titleKey: 'home.mapTitle',
        route: 'events/map',
        type: 'modal',
        menuHidden: true
    },
    {
        id: 'subscribe',
        icon: '/images/subscribe-grey.svg',
        titleKey: 'home.subscribeTitle',
        route: 'subscribe',
        type: 'modal'
    },
    {
        id: 'eventEditor',
        icon: '/images/event-add-grey.svg',
        titleKey: 'home.addTitle',
        route: 'events/add',
        type: 'modal'
    },
    {
        id: 'contact',
        icon: '/images/contact-grey.svg',
        titleKey: 'home.contactTitle',
        route: 'contact',
        type: 'modal'
    },
    {
        id: 'signin',
        icon: '/images/signin-grey.svg',
        titleKey: 'home.signinTitle',
        route: 'signin',
        type: 'modal',
        authenticatedId: 'account',
        authenticatedIcon: '/images/account-grey.svg',
        authenticatedTitleKey: 'home.accountTitle',
        authenticatedRoute: 'account'
    }
];

export class Home {
    constructor(app) {
        this.app = app;
        this.locale = app.locale || document.documentElement.lang || 'fr';
        this.root = document.getElementById('home');
        this.grid = this.root?.querySelector('.home-grid') || null;
        this.logo = null;
        this.filterButton = null;
        this.stat = null;
        this.activeView = 'list';
        this.viewHosts = new Map();
        this.viewReady = new Map();
        this.menu = null;
        this.menuButton = null;
        this.menuOpen = false;
        this.routeSyncInProgress = false;
        this.initialRouteHandled = false;
        this.boundPopstate = () => this.handleLocationRoute();
        this.boundDocumentPointer = (event) => this.handleDocumentPointer(event);
    }

    init() {
        if (!this.root || !this.grid) {
            return false;
        }

        this.renderShell();
        this.registerContents();
        this.mountApplicationModals();
        this.updateAuthState();
        this.updateFilterBadge();
        locales.createMenu(this.app, {
            container: this.root.querySelector('.home-language')
        });
        window.addEventListener('popstate', this.boundPopstate);
        document.addEventListener('pointerdown', this.boundDocumentPointer);
        return true;
    }

    destroy() {
        window.removeEventListener('popstate', this.boundPopstate);
        document.removeEventListener('pointerdown', this.boundDocumentPointer);
    }

    renderShell() {
        const header = document.createElement('header');
        const toolbar = document.createElement('div');
        const language = document.createElement('div');
        const eventCount = document.createElement('span');
        const tabs = document.createElement('nav');
        const panels = document.createElement('div');
        const footer = this.root.querySelector('.home-copyright');
        const copyright = document.createElement('span');
        const version = document.createElement('span');

        header.className = 'home-header';
        toolbar.className = 'home-toolbar';
        language.className = 'home-language';
        language.setAttribute('aria-label', translate('localeMenu.change', this.locale));
        eventCount.className = 'home-event-count';
        eventCount.dataset.eventCount = 'header';
        eventCount.hidden = true;
        tabs.className = 'home-tabs';
        tabs.setAttribute('aria-label', translate('mainMenu.ariaLabel', this.locale));
        panels.className = 'home-tab-panels';

        this.logo = this.createLogo();
        this.filterButton = this.createFilterButton();
        this.menuButton = this.createMenuButton();
        toolbar.append(language, this.filterButton, this.menuButton);
        tabs.append(
            this.createTab('list', 'home.listTitle', ''),
            this.createTab('calendar', 'home.calendarTitle', 'events/calendar')
        );
        panels.append(
            this.createViewPanel('list'),
            this.createViewPanel('calendar')
        );
        header.append(this.logo, toolbar, eventCount, tabs);
        this.menu = this.createNavigationMenu();
        this.grid.replaceChildren(
            header,
            panels,
            this.menu
        );

        copyright.className = 'home-copyright-label';
        copyright.textContent = '©2026 BUDO Events™ & SHUDOKAN';
        version.className = 'home-version';
        version.append('Version ', this.createVersionValue());
        footer?.replaceChildren(copyright, version);
    }

    createVersionValue() {
        const value = document.createElement('span');

        value.className = 'app-version';
        value.textContent = this.app.config?.version || '';
        return value;
    }

    createLogo() {
        const container = document.createElement('div');
        const image = document.createElement('img');

        container.className = 'logo';
        image.src = '/images/logo-white.svg';
        image.alt = 'BUDO Events™';
        container.append(image);

        return container;
    }

    createMenuButton() {
        const button = document.createElement('button');
        const label = document.createElement('span');
        const icon = document.createElement('img');

        button.type = 'button';
        button.className = 'home-menu-button';
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-controls', 'home-navigation');
        button.setAttribute('aria-label', translate('mainMenu.open', this.locale));
        label.textContent = translate('mainMenu.menu', this.locale);
        icon.src = '/images/burger-white.svg';
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
        button.append(label, icon);
        button.addEventListener('click', () => this.toggleMenu());

        return button;
    }

    createFilterButton() {
        const button = document.createElement('button');
        const label = document.createElement('span');
        const icon = document.createElement('img');

        button.type = 'button';
        button.className = 'home-filter-button';
        button.setAttribute('aria-label', translate('home.filtersTitle', this.locale));
        label.textContent = translate('home.filtersTitle', this.locale);
        icon.src = '/images/filter-white.svg';
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
        button.append(label, icon);
        button.addEventListener('click', () => {
            this.closeMenu();
            this.openAction('eventsFilters', button);
        });

        return button;
    }

    createTab(view, titleKey, route) {
        const link = document.createElement('a');
        const label = document.createElement('span');

        link.className = 'home-tab';
        link.dataset.homeView = view;
        link.dataset.homeAction = view === 'list' ? 'eventsList' : 'eventsCalendar';
        link.href = route ? `/${this.locale}/${route}` : `/${this.locale}/`;
        link.setAttribute('role', 'tab');
        link.setAttribute('aria-controls', `home-${view}-panel`);
        link.setAttribute('aria-selected', String(view === this.activeView));
        label.className = 'home-tab-label';
        label.textContent = translate(titleKey, this.locale);
        link.append(label);

        link.addEventListener('click', (event) => {
            event.preventDefault();
            void this.showTab(view, { reload: view === 'list' });
        });

        return link;
    }

    createViewPanel(view) {
        const panel = document.createElement('section');
        const loading = document.createElement('p');
        const host = document.createElement('div');

        panel.id = `home-${view}-panel`;
        panel.className = `home-tab-panel home-tab-panel-${view}`;
        panel.dataset.homeViewPanel = view;
        panel.setAttribute('role', 'tabpanel');
        panel.hidden = view !== this.activeView;
        loading.className = 'home-view-loading';
        loading.textContent = translate('simpleModal.loading', this.locale);
        loading.hidden = true;
        loading.setAttribute('role', 'status');
        loading.setAttribute('aria-live', 'polite');
        host.className = 'home-view-host';
        host.dataset.homeViewHost = view;
        panel.append(loading, host);
        this.viewHosts.set(view, host);

        return panel;
    }

    createNavigationMenu() {
        const navigation = document.createElement('nav');
        const list = document.createElement('div');

        navigation.id = 'home-navigation';
        navigation.className = 'home-navigation';
        navigation.setAttribute('aria-label', translate('mainMenu.ariaLabel', this.locale));
        navigation.setAttribute('aria-hidden', 'true');
        list.className = 'home-navigation-list';

        HOME_NAVIGATION.filter((action) => !action.menuHidden).forEach((action) => {
            list.append(this.createNavigationItem(action));
            if (action.authenticatedId) {
                list.append(this.createNavigationItem({
                    ...action,
                    id: action.authenticatedId,
                    icon: action.authenticatedIcon,
                    titleKey: action.authenticatedTitleKey,
                    route: action.authenticatedRoute,
                    authenticatedOnly: true
                }));
            }
        });
        navigation.append(list);

        return navigation;
    }

    createNavigationItem(action) {
        const item = document.createElement('a');
        const icon = document.createElement('img');
        const label = document.createElement('span');

        item.className = 'home-navigation-item';
        item.dataset.homeAction = action.id;
        item.dataset.menuItem = action.id;
        item.href = action.route ? `/${this.locale}/${action.route}` : `/${this.locale}/`;
        item.hidden = Boolean(action.authenticatedOnly);
        icon.src = action.icon;
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
        label.textContent = translate(action.titleKey, this.locale);
        item.append(icon, label);
        item.addEventListener('click', (event) => {
            event.preventDefault();
            this.closeMenu();
            this.openAction(action.id, item);
        });

        return item;
    }

    registerContents() {
        Object.assign(this.app.simpleModal.contents, {
            eventsFilters: {
                titleKey: 'home.filtersTitle',
                icon: '/images/filter-grey.svg',
                path: 'events/filters',
                render: (context) => this.renderFilters(context)
            },
            eventsMap: {
                titleKey: 'home.mapTitle',
                icon: '/images/marker-grey.svg',
                path: 'events/map',
                render: ({ body }) => {
                    body.classList.add('home-map-modal-body');
                    return true;
                }
            }
        });
    }

    mountApplicationModals() {
        HOME_NAVIGATION
            .filter((action) => action.type === 'modal')
            .forEach((action) => this.app.simpleModal.mount(action.id));
        this.app.simpleModal.mount('eventsFilters');
        this.app.simpleModal.mount('signup');
        this.app.simpleModal.mount('account');
    }

    toggleMenu(force = !this.menuOpen) {
        if (force) {
            this.openMenu();
        } else {
            this.closeMenu();
        }
    }

    openMenu() {
        if (!this.menu || this.menuOpen) return false;
        this.menuOpen = true;
        this.menu.classList.add('is-open');
        this.menu.setAttribute('aria-hidden', 'false');
        this.menuButton?.setAttribute('aria-expanded', 'true');
        return true;
    }

    closeMenu() {
        if (!this.menu || !this.menuOpen) return false;
        this.menuOpen = false;
        this.menu.classList.remove('is-open');
        this.menu.setAttribute('aria-hidden', 'true');
        this.menuButton?.setAttribute('aria-expanded', 'false');
        return true;
    }

    handleDocumentPointer(event) {
        if (!this.menuOpen || this.menu?.contains(event.target) || this.menuButton?.contains(event.target)) {
            return;
        }
        this.closeMenu();
    }

    openAction(contentId, trigger = null, options = {}) {
        if (contentId === 'eventsList') {
            return this.showTab('list', { ...options, reload: true });
        }
        if (contentId === 'eventsCalendar') {
            return this.showTab('calendar', options);
        }

        let targetId = contentId;

        if (contentId === 'eventEditor') {
            if (!this.app.events?.requireAuthentication()) {
                return false;
            }
            this.app.events.editor.event = null;
            this.app.simpleModal.contents.eventEditor = this.app.events.editor.getContents().eventEditor;
        }

        if (contentId === 'signin' && this.app.user) {
            targetId = 'account';
        }

        const opened = this.app.simpleModal.open(targetId, trigger);

        if (opened && !options.skipRoute) {
            this.navigateToContent(targetId, options.replaceRoute);
        }

        return opened;
    }

    async showTab(view, options = {}) {
        if (!['list', 'calendar'].includes(view)) {
            return false;
        }

        const panel = this.root.querySelector(`[data-home-view-panel="${view}"]`);
        const host = this.viewHosts.get(view);

        if (!panel || !host) {
            return false;
        }

        this.activeView = view;
        this.closeMenu();
        this.scrollViewportToTop();
        this.root.querySelectorAll('[data-home-view]').forEach((tab) => {
            const active = tab.dataset.homeView === view;

            tab.classList.toggle('is-active', active);
            tab.setAttribute('aria-selected', String(active));
        });
        this.root.querySelectorAll('[data-home-view-panel]').forEach((candidate) => {
            candidate.hidden = candidate !== panel;
        });

        if (!this.viewReady.get(view)) {
            await this.mountEventsView(view, panel, host);
        } else if (view === 'list' && options.reload) {
            await this.reloadListView(panel);
        }
        this.app.events.activateView(view);

        if (!options.skipRoute) {
            this.navigateToContent(view === 'list' ? 'eventsList' : 'eventsCalendar', options.replaceRoute);
        } else {
            this.updateMetadata(view === 'calendar' ? 'eventsCalendar' : null);
        }

        return true;
    }

    scrollViewportToTop() {
        const scroller = document.scrollingElement;

        if (!scroller) {
            return false;
        }

        scroller.scrollTop = 0;
        return true;
    }

    async reloadListView(panel) {
        try {
            await this.app.events.list({}, {
                before: () => this.setViewLoading(panel, true),
                success: () => this.setViewLoading(panel, false),
                error: () => this.setViewLoading(panel, false)
            });
        } finally {
            this.setViewLoading(panel, false);
        }

        if (!this.app.events.loaded) {
            return false;
        }

        this.scrollViewportToTop();
        this.updateEventCount();
        return true;
    }

    async mountEventsView(view, panel, host) {
        this.setViewLoading(panel, true);
        try {
            await this.app.events.ensureLoaded();
        } finally {
            this.setViewLoading(panel, false);
        }

        if (!this.app.events.loaded) {
            return false;
        }

        this.app.events.mountView(view, host);
        this.viewReady.set(view, true);
        this.updateEventCount();
        return true;
    }

    setViewLoading(panel, active) {
        const loading = panel?.querySelector('.home-view-loading');

        if (!panel || !loading) {
            return false;
        }

        panel.toggleAttribute('aria-busy', active);
        loading.hidden = !active;
        return true;
    }

    async renderFilters(context) {
        context.modal.classList.add('home-events-modal', 'home-events-modal-filters');
        await this.app.events.ensureLoaded({}, {
            before: context.showLoading,
            success: context.hideLoading,
            error: context.hideLoading
        });

        if (!this.app.events.loaded) {
            return false;
        }

        this.app.events.mountView('filters', context.body);
        this.updateFilterBadge();
        return true;
    }

    loadFilteredEventsInBackground() {
        return false;
    }

    async loadStats() {
        const succeeded = await this.app.api.query(
            'get',
            '/events/stat',
            {},
            null,
            (result) => {
                this.stat = result?.result || null;
                this.app.events?.setDateBounds?.(this.stat?.first_date, this.stat?.last_date);
                this.updateEventCount();
            },
            null,
            {
                onError: () => {
                    this.stat = null;
                    this.updateEventCount();
                }
            }
        );

        return succeeded ? this.stat : null;
    }

    updateEventCount(count = this.stat?.count ?? null) {
        const badge = this.root.querySelector('[data-event-count]');
        const loadedEvents = this.app.events?.loaded ? this.app.events.events : null;
        const total = Number(loadedEvents ? loadedEvents.length : count);
        const filtered = loadedEvents
            ? this.app.events.getFilteredEvents().length
            : total;

        if (!badge) return false;
        badge.hidden = (!loadedEvents && count === null) || !Number.isFinite(total) || total < 0;
        if (badge.hidden) return false;

        const formattedFiltered = new Intl.NumberFormat(this.locale).format(filtered);
        const formattedTotal = new Intl.NumberFormat(this.locale).format(total);
        const activeFilterCount = this.app.events?.getActiveFilterCount?.() || 0;
        const totalLine = this.createEventCountLine(
            'home.eventCountLine',
            formattedTotal,
            'home-event-count-total'
        );
        const lines = [totalLine];
        const ariaLabels = [
            translate('home.eventCountLine', this.locale, { count: formattedTotal })
        ];

        if (activeFilterCount > 0) {
            lines.push(this.createEventCountLine(
                'home.filteredEventCountLine',
                formattedFiltered,
                'home-event-count-filtered'
            ));
            ariaLabels.push(translate(
                'home.filteredEventCountLine',
                this.locale,
                { count: formattedFiltered }
            ));
        }

        badge.replaceChildren(...lines);
        badge.setAttribute('aria-label', ariaLabels.join('. '));
        return true;
    }

    createEventCountLine(key, count, className) {
        const line = document.createElement('span');
        const value = document.createElement('strong');
        const template = translate(key, this.locale);
        const [prefix = '', suffix = ''] = template.split('{count}');

        line.className = `home-event-count-line ${className}`;
        value.className = 'home-event-count-value';
        value.textContent = count;
        line.append(prefix, value, suffix);
        return line;
    }

    updateAuthState() {
        const authenticated = Boolean(this.app.user);

        this.root.querySelectorAll('[data-menu-item="signin"]').forEach((item) => {
            item.hidden = authenticated;
        });
        this.root.querySelectorAll('[data-menu-item="account"]').forEach((item) => {
            item.hidden = !authenticated;
        });
    }

    updateFilterBadge() {
        const count = this.app.events?.getActiveFilterCount?.() || 0;

        document.querySelectorAll('[data-filter-badge]').forEach((badge) => {
            badge.hidden = count === 0;
            badge.textContent = String(count);
            badge.setAttribute('aria-label', translate('filters.activeCount', this.locale, { count }));
        });
        this.updateEventCount();
    }

    refreshActiveView() {
        const container = this.app.events?.viewContainers?.get(this.activeView);
        const panel = this.root.querySelector(`[data-home-view-panel="${this.activeView}"]`);

        if (!container?.isConnected) {
            return false;
        }

        panel?.setAttribute('aria-busy', 'true');
        try {
            this.app.events.renderView(this.activeView, container);
        } finally {
            panel?.removeAttribute('aria-busy');
        }
        this.updateFilterBadge();
        return true;
    }

    getContentPath(contentId) {
        const base = `/${this.locale}`;
        const paths = {
            about: 'about',
            eventsCalendar: 'events/calendar',
            eventsMap: 'events/map',
            eventsFilters: 'events/filters',
            subscribe: 'subscribe',
            eventEditor: 'events/add',
            contact: 'contact',
            signin: 'signin',
            signup: 'signup',
            account: 'account'
        };

        if (contentId === 'eventsList' || !contentId) {
            return `${base}/`;
        }
        return paths[contentId] ? `${base}/${paths[contentId]}` : `${base}/`;
    }

    navigateToContent(contentId, replace = false) {
        const path = this.getContentPath(contentId);
        const method = replace ? 'replaceState' : 'pushState';

        if (window.location.pathname !== path) {
            window.history[method]({ contentId }, '', path);
        }
        this.updateMetadata(contentId === 'eventsList' ? null : contentId);
    }

    navigateToEvent(event, replace = false) {
        const slug = this.getEventSlug(event);
        const path = `/${this.locale}/events/${encodeURIComponent(slug)}`;
        const method = replace ? 'replaceState' : 'pushState';

        if (window.location.pathname !== path) {
            window.history[method]({ event: event?.uid || slug }, '', path);
        }
        document.title = `${this.app.events.getLocalizedValue(event?.name)} — BUDO Events™`;
        this.syncLocalizedLinks(event);
    }

    restoreUnderlyingRoute() {
        const previousModal = this.app.simpleModal.modalStack.at(-1);
        const contentId = previousModal?.contentId;

        if (contentId) {
            this.navigateToContent(contentId, true);
            return;
        }

        this.navigateToContent(
            this.activeView === 'calendar' ? 'eventsCalendar' : 'eventsList',
            true
        );
    }

    handleModalClosed(activeModal) {
        if (this.routeSyncInProgress) return;
        if (activeModal.contentId === 'eventsFilters') {
            this.refreshActiveView();
        }
        if (activeModal.contentId === 'eventDetails') {
            this.restoreUnderlyingRoute();
            return;
        }

        const currentPath = this.getContentPath(activeModal.contentId);

        if (window.location.pathname === currentPath) {
            this.restoreUnderlyingRoute();
        }
    }

    async handleInitialRoute() {
        if (this.initialRouteHandled) return;
        this.initialRouteHandled = true;

        await this.handleLocationRoute({ initial: true });
    }

    async handleLocationRoute({ initial = false } = {}) {
        const route = this.parseRoute(window.location.pathname);

        if (!route) return;
        this.closeMenu();

        if (route.type === 'view') {
            if (!initial) this.closeAllModalsForRoute();
            if (route.replace) {
                window.history.replaceState({}, '', this.getContentPath('eventsList'));
            }
            await this.showTab(route.view, { skipRoute: true });
            return;
        }

        this.closeAllModalsForRoute();
        if (route.type === 'event') {
            await this.openEventRoute(route.slug);
            return;
        }

        const trigger = this.root.querySelector(`[data-home-action="${route.contentId}"]`);
        const opened = this.openAction(route.contentId, trigger, { skipRoute: true });
        const openedContentId = opened
            ? this.app.simpleModal.modalStack.at(-1)?.contentId
            : null;

        if (!opened && route.contentId === 'eventEditor') {
            this.navigateToContent(
                this.activeView === 'calendar' ? 'eventsCalendar' : 'eventsList',
                true
            );
            return;
        }

        if (openedContentId && openedContentId !== route.contentId) {
            this.navigateToContent(openedContentId, true);
            return;
        }

        this.updateMetadata(openedContentId || route.contentId);
    }

    closeAllModalsForRoute() {
        this.routeSyncInProgress = true;
        [...this.app.simpleModal.modalStack].reverse().forEach((modal) => {
            this.app.simpleModal.close(modal);
        });
        this.routeSyncInProgress = false;
    }

    parseRoute(pathname) {
        const escapedLocale = this.locale.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const relative = pathname.replace(new RegExp(`^/${escapedLocale}/?`), '').replace(/\/+$/, '');
        const routes = {
            about: 'about',
            'events/filters': 'eventsFilters',
            'events/map': 'eventsMap',
            subscribe: 'subscribe',
            'events/add': 'eventEditor',
            contact: 'contact',
            signin: 'signin',
            signup: 'signup',
            account: 'account'
        };

        if (!relative) return { type: 'view', view: 'list' };
        if (relative === 'events/list') return { type: 'view', view: 'list', replace: true };
        if (relative === 'events/calendar') return { type: 'view', view: 'calendar' };
        if (routes[relative]) return { type: 'content', contentId: routes[relative] };
        if (relative.startsWith('events/')) {
            return { type: 'event', slug: relative.slice('events/'.length) };
        }

        return null;
    }

    async openEventRoute(slug) {
        let canonicalName = '';

        try {
            canonicalName = decodeURIComponent(String(slug || '')).trim();
        } catch {
            canonicalName = '';
        }

        if (!canonicalName) {
            new Notification({
                icon: 'warning.svg',
                title: translate('home.eventNotFoundTitle', this.locale),
                content: translate('home.eventNotFoundMessage', this.locale)
            });
            return false;
        }

        return this.app.events.openEventDetailsByCanonicalName(
            canonicalName,
            { preserveRoute: true }
        );
    }

    getEventSlug(event, locale = this.locale) {
        const canonicalNames = event?.canonical_name;
        const canonicalName = typeof canonicalNames === 'string'
            ? canonicalNames
            : canonicalNames?.[locale]
                || canonicalNames?.[String(locale).split('-')[0]]
                || canonicalNames?.und
                || Object.values(canonicalNames || {}).find(Boolean);

        if (canonicalName) {
            return String(canonicalName);
        }

        const name = this.app.events?.getLocalizedValue(event?.name) || 'event';
        const normalized = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const slug = normalized
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .slice(0, 100);

        return slug || 'event';
    }

    updateMetadata(contentId) {
        const definition = this.app.simpleModal.contents[contentId];
        const title = definition?.titleKey
            ? translate(definition.titleKey, this.locale)
            : this.app.config?.title?.[this.locale] || 'BUDO Events™';

        document.title = contentId ? `${title} — BUDO Events™` : title;
        this.syncLocalizedLinks();
    }

    syncLocalizedLinks(event = null) {
        const currentPath = window.location.pathname;
        const relativePath = currentPath.replace(/^\/[^/]+/, '');
        const pathSuffix = relativePath || '/';
        const canonical = document.querySelector('link[rel="canonical"]');
        const currentLocaleLink = this.root?.querySelector('.locales-menu-current');
        const absoluteUrl = (path) => new URL(path, 'https://www.budo.events').href;
        const getLocalizedPath = (locale) => event
            ? `/${locale}/events/${encodeURIComponent(this.getEventSlug(event, locale))}`
            : `/${locale}${pathSuffix}`;

        canonical?.setAttribute('href', absoluteUrl(currentPath));
        if (currentLocaleLink) {
            const locale = currentLocaleLink.hreflang || this.locale;
            currentLocaleLink.href = event
                ? getLocalizedPath(locale)
                : locales.getLocaleUrl(locale);
        }

        this.root?.querySelectorAll('.locales-menu-item[data-locale]').forEach((link) => {
            link.href = event
                ? getLocalizedPath(link.dataset.locale)
                : locales.getLocaleUrl(link.dataset.locale);
        });

        document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((link) => {
            const locale = link.getAttribute('hreflang');
            const targetLocale = locale === 'x-default' ? 'fr' : locale;

            link.setAttribute('href', absoluteUrl(getLocalizedPath(targetLocale)));
        });
    }
}
